import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '@/lib/supabase'
import { createDogNameMap } from '@/lib/dogNames'
import { useDateFilter } from './useDateFilter'
import type { DatePeriod } from '@/components/dashboard/filters'
import type { Grouping } from '@/components/dashboard/filters'
import type { VisitorRow, VisitorStatus } from '@/types/dashboard'
import type { Session, RevenueEvent } from '@/types/database'
import type { DrillDownFilter } from '@/types/filters'
import { format, parseISO, startOfDay, startOfWeek } from 'date-fns'
import type { KPIData } from '@/components/dashboard/kpi'
import type { TimeSeriesDataPoint } from '@/components/dashboard/charts'
import type { DistributionDataItem } from '@/components/dashboard/charts'

const LIVE_THRESHOLD_SECONDS = 20
const IDLE_THRESHOLD_SECONDS = 30

export interface EnhancedDistributions {
  // Traffic Sources
  referrers: DistributionDataItem[]
  campaigns: DistributionDataItem[]
  keywords: DistributionDataItem[]
  paths: DistributionDataItem[]
  hostnames: DistributionDataItem[]
  entryPages: DistributionDataItem[]
  exitPages: DistributionDataItem[]
  // Geography
  countries: DistributionDataItem[]
  regions: DistributionDataItem[]
  cities: DistributionDataItem[]
  // Technology
  browsers: DistributionDataItem[]
  operatingSystems: DistributionDataItem[]
  devices: DistributionDataItem[]
}

export interface EnhancedDashboardData {
  kpis: KPIData
  timeSeries: TimeSeriesDataPoint[]
  distributions: EnhancedDistributions
  visitors: VisitorRow[]
  isLoading: boolean
  error: Error | null
  refresh: () => Promise<void>
}

// Helper function to get referrer source (same logic as in fetchData)
function getReferrerSourceForFilter(referrer: string | null): string {
  if (!referrer) return 'Direct / None'
  try {
    const url = new URL(referrer)
    const hostname = url.hostname.replace('www.', '')
    if (hostname.includes('google')) return 'Google'
    if (hostname.includes('facebook') || hostname.includes('fb.com')) return 'Facebook'
    if (hostname.includes('twitter') || hostname.includes('t.co') || hostname.includes('x.com')) return 'X'
    if (hostname.includes('instagram')) return 'Instagram'
    if (hostname.includes('youtube')) return 'YouTube'
    if (hostname.includes('linkedin')) return 'LinkedIn'
    if (hostname.includes('tiktok')) return 'TikTok'
    if (hostname.includes('reddit')) return 'Reddit'
    return hostname
  } catch {
    return referrer || 'Direct / None'
  }
}

// Helper to check if a value matches the filter, handling "Unknown" for null/empty values
function matchesFilterValue(sessionValue: string | null | undefined, filterValue: string): boolean {
  if (filterValue === 'Unknown') {
    return !sessionValue || sessionValue === 'Unknown'
  }
  return sessionValue === filterValue
}

// Filter sessions based on drill-down filter
function filterSessionsByDrillDown(
  sessions: Session[],
  filter: DrillDownFilter
): Session[] {
  return sessions.filter((session) => {
    switch (filter.type) {
      case 'visitor_hash':
        return session.visitor_hash === filter.value
      case 'country_code':
        return matchesFilterValue(session.country_code, filter.value)
      case 'city':
        return matchesFilterValue(session.city, filter.value)
      case 'browser':
        return matchesFilterValue(session.browser, filter.value)
      case 'os':
        return matchesFilterValue(session.os, filter.value)
      case 'device_type':
        return matchesFilterValue(session.device_type, filter.value)
      case 'referrer_source':
        return getReferrerSourceForFilter(session.referrer) === filter.value
      case 'utm_campaign':
        return matchesFilterValue(session.utm_campaign, filter.value)
      case 'utm_term':
        return matchesFilterValue(session.utm_term, filter.value)
      case 'entry_page':
        return matchesFilterValue(session.entry_page, filter.value)
      default:
        return true
    }
  })
}

export function useEnhancedDashboardData(
  siteId: string | undefined,
  period: DatePeriod = 'last30d',
  grouping: Grouping = 'daily',
  drillDownFilter?: DrillDownFilter | null
): EnhancedDashboardData {
  const dateFilter = useDateFilter(period, grouping)

  const [kpis, setKpis] = useState<KPIData>({
    visitors: 0,
    visitorsPrevious: 0,
    revenue: 0,
    revenuePrevious: 0,
    revenueConversionRate: 0,
    revenueConversionRatePrevious: 0,
    revenuePerVisitor: 0,
    revenuePerVisitorPrevious: 0,
    bounceRate: 0,
    bounceRatePrevious: 0,
    avgSessionDuration: 0,
    avgSessionDurationPrevious: 0,
    onlineCount: 0,
  })

  const [timeSeries, setTimeSeries] = useState<TimeSeriesDataPoint[]>([])
  const [distributions, setDistributions] = useState<EnhancedDistributions>({
    referrers: [],
    campaigns: [],
    keywords: [],
    paths: [],
    hostnames: [],
    entryPages: [],
    exitPages: [],
    countries: [],
    regions: [],
    cities: [],
    browsers: [],
    operatingSystems: [],
    devices: [],
  })
  const [visitors, setVisitors] = useState<VisitorRow[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const fetchDataRef = useRef<() => Promise<void>>()

  const fetchData = useCallback(async () => {
    if (!siteId) return

    try {
      setError(null)
      const now = new Date()
      const liveThreshold = new Date(now.getTime() - LIVE_THRESHOLD_SECONDS * 1000).toISOString()

      // Fetch current period sessions
      const { data: sessionsData, error: sessionsError } = await supabase
        .from('sessions')
        .select('*')
        .eq('site_id', siteId)
        .gte('started_at', dateFilter.startDateISO)
        .lte('started_at', dateFilter.endDateISO)
        .eq('is_bot', false)
        .eq('is_self', false)
        .order('started_at', { ascending: false })
        .limit(500)

      if (sessionsError) throw sessionsError

      // Fetch previous period sessions for comparison
      const { data: prevSessionsData } = await supabase
        .from('sessions')
        .select('id, visitor_hash, pageview_count, started_at, last_seen_at')
        .eq('site_id', siteId)
        .gte('started_at', dateFilter.previousStartDateISO)
        .lte('started_at', dateFilter.previousEndDateISO)
        .eq('is_bot', false)
        .eq('is_self', false)
        .limit(500)

      // Fetch revenue data for current period
      const { data: revenueData } = await supabase
        .from('revenue_events')
        .select('*')
        .eq('site_id', siteId)
        .gte('timestamp', dateFilter.startDateISO)
        .lte('timestamp', dateFilter.endDateISO)

      // Fetch revenue data for previous period
      const { data: prevRevenueData } = await supabase
        .from('revenue_events')
        .select('amount_cents')
        .eq('site_id', siteId)
        .gte('timestamp', dateFilter.previousStartDateISO)
        .lte('timestamp', dateFilter.previousEndDateISO)

      // Fetch online count
      const { count: onlineCount } = await supabase
        .from('sessions')
        .select('*', { count: 'exact', head: true })
        .eq('site_id', siteId)
        .gte('last_seen_at', liveThreshold)
        .eq('is_bot', false)
        .eq('is_self', false)

      let sessions = (sessionsData || []) as Session[]
      const prevSessions = prevSessionsData || []
      let revenue = (revenueData || []) as RevenueEvent[]
      const prevRevenue = (prevRevenueData || []) as Pick<RevenueEvent, 'amount_cents'>[]

      // Apply drill-down filter if active
      if (drillDownFilter) {
        sessions = filterSessionsByDrillDown(sessions, drillDownFilter)
        // Filter revenue to only include visitors in filtered sessions
        const filteredVisitorHashes = new Set(sessions.map((s) => s.visitor_hash))
        revenue = revenue.filter((r) => filteredVisitorHashes.has(r.visitor_hash))
      }

      // Calculate current period metrics
      const uniqueVisitors = new Set(sessions.map((s) => s.visitor_hash)).size
      const totalRevenue = revenue.reduce((sum, r) => sum + (r.amount_cents || 0), 0) / 100
      const revenueConversions = revenue.length
      const revenueConversionRate = uniqueVisitors > 0 ? (revenueConversions / uniqueVisitors) * 100 : 0
      const revenuePerVisitor = uniqueVisitors > 0 ? totalRevenue / uniqueVisitors : 0

      // Bounce rate: sessions with only 1 pageview
      const bouncedSessions = sessions.filter((s) => s.pageview_count <= 1).length
      const bounceRate = sessions.length > 0 ? (bouncedSessions / sessions.length) * 100 : 0

      // Average session duration
      const durations = sessions
        .map((s) => {
          const start = new Date(s.started_at).getTime()
          const end = s.last_seen_at ? new Date(s.last_seen_at).getTime() : start
          return (end - start) / 1000
        })
        .filter((d) => d > 0)
      const avgSessionDuration = durations.length > 0
        ? durations.reduce((a, b) => a + b, 0) / durations.length
        : 0

      // Calculate previous period metrics
      const prevUniqueVisitors = new Set(prevSessions.map((s: any) => s.visitor_hash)).size
      const prevTotalRevenue = prevRevenue.reduce((sum: number, r: any) => sum + (r.amount_cents || 0), 0) / 100
      const prevRevenueConversionRate = prevUniqueVisitors > 0 ? (prevRevenue.length / prevUniqueVisitors) * 100 : 0
      const prevRevenuePerVisitor = prevUniqueVisitors > 0 ? prevTotalRevenue / prevUniqueVisitors : 0
      const prevBouncedSessions = prevSessions.filter((s: any) => s.pageview_count <= 1).length
      const prevBounceRate = prevSessions.length > 0 ? (prevBouncedSessions / prevSessions.length) * 100 : 0
      const prevDurations = prevSessions
        .map((s: any) => {
          const start = new Date(s.started_at).getTime()
          const end = s.last_seen_at ? new Date(s.last_seen_at).getTime() : start
          return (end - start) / 1000
        })
        .filter((d: number) => d > 0)
      const prevAvgSessionDuration = prevDurations.length > 0
        ? prevDurations.reduce((a: number, b: number) => a + b, 0) / prevDurations.length
        : 0

      setKpis({
        visitors: uniqueVisitors,
        visitorsPrevious: prevUniqueVisitors,
        revenue: totalRevenue,
        revenuePrevious: prevTotalRevenue,
        revenueConversionRate,
        revenueConversionRatePrevious: prevRevenueConversionRate,
        revenuePerVisitor,
        revenuePerVisitorPrevious: prevRevenuePerVisitor,
        bounceRate,
        bounceRatePrevious: prevBounceRate,
        avgSessionDuration,
        avgSessionDurationPrevious: prevAvgSessionDuration,
        onlineCount: onlineCount || 0,
      })

      // Generate time series data
      const timeSeriesMap = new Map<string, { visitors: Set<string>; revenue: number }>()

      // Initialize all dates in the period
      for (const dateStr of dateFilter.dateLabels) {
        timeSeriesMap.set(dateStr, { visitors: new Set(), revenue: 0 })
      }

      // Aggregate sessions by date
      for (const session of sessions) {
        const dateKey = grouping === 'weekly'
          ? format(startOfWeek(parseISO(session.started_at), { weekStartsOn: 1 }), 'yyyy-MM-dd')
          : format(startOfDay(parseISO(session.started_at)), 'yyyy-MM-dd')

        const entry = timeSeriesMap.get(dateKey)
        if (entry) {
          entry.visitors.add(session.visitor_hash)
        }
      }

      // Aggregate revenue by date
      for (const rev of revenue) {
        const dateKey = grouping === 'weekly'
          ? format(startOfWeek(parseISO(rev.timestamp), { weekStartsOn: 1 }), 'yyyy-MM-dd')
          : format(startOfDay(parseISO(rev.timestamp)), 'yyyy-MM-dd')

        const entry = timeSeriesMap.get(dateKey)
        if (entry) {
          entry.revenue += (rev.amount_cents || 0) / 100
        }
      }

      const timeSeriesData: TimeSeriesDataPoint[] = dateFilter.dateLabels.map((date) => {
        const entry = timeSeriesMap.get(date)
        return {
          date,
          visitors: entry?.visitors.size || 0,
          revenue: entry?.revenue || 0,
        }
      })

      setTimeSeries(timeSeriesData)

      // Create a map of visitor_hash to session field values for revenue attribution
      const visitorFieldValues = new Map<string, Map<string, string>>()
      for (const session of sessions) {
        if (!visitorFieldValues.has(session.visitor_hash)) {
          visitorFieldValues.set(session.visitor_hash, new Map())
        }
        const fieldMap = visitorFieldValues.get(session.visitor_hash)!
        // Store first occurrence of each field per visitor
        if (!fieldMap.has('country_code') && session.country_code) {
          fieldMap.set('country_code', session.country_code)
        }
        if (!fieldMap.has('browser') && session.browser) {
          fieldMap.set('browser', session.browser)
        }
        if (!fieldMap.has('os') && session.os) {
          fieldMap.set('os', session.os)
        }
        if (!fieldMap.has('device_type') && session.device_type) {
          fieldMap.set('device_type', session.device_type)
        }
        if (!fieldMap.has('referrer') && session.referrer) {
          fieldMap.set('referrer', session.referrer)
        }
        if (!fieldMap.has('utm_campaign') && session.utm_campaign) {
          fieldMap.set('utm_campaign', session.utm_campaign)
        }
        if (!fieldMap.has('entry_page') && session.entry_page) {
          fieldMap.set('entry_page', session.entry_page)
        }
      }

      // Build distributions with revenue data
      const buildDistributionWithRevenue = (
        field: keyof Session
      ): DistributionDataItem[] => {
        const dataMap = new Map<string, { visitors: number; revenue: number; conversions: number }>()

        for (const session of sessions) {
          const value = String(session[field] || 'Unknown')
          if (!dataMap.has(value)) {
            dataMap.set(value, { visitors: 0, revenue: 0, conversions: 0 })
          }
          dataMap.get(value)!.visitors++
        }

        // Add revenue data from converted visitors
        for (const rev of revenue) {
          const fieldMap = visitorFieldValues.get(rev.visitor_hash)
          if (fieldMap) {
            const value = fieldMap.get(field as string) || 'Unknown'
            const entry = dataMap.get(value)
            if (entry) {
              entry.revenue += (rev.amount_cents || 0) / 100
              entry.conversions++
            }
          }
        }

        return Array.from(dataMap.entries())
          .map(([label, data]) => ({
            label,
            visitors: data.visitors,
            revenue: data.revenue,
            conversions: data.conversions,
            conversionRate: data.visitors > 0 ? (data.conversions / data.visitors) * 100 : 0,
            revenuePerVisitor: data.visitors > 0 ? data.revenue / data.visitors : 0,
          }))
          .sort((a, b) => b.visitors - a.visitors)
          .slice(0, 20)
      }

      // Extract referrer source from referrer URL
      const getReferrerSource = (referrer: string | null): string => {
        if (!referrer) return 'Direct / None'
        try {
          const url = new URL(referrer)
          const hostname = url.hostname.replace('www.', '')
          // Map common hostnames to friendly names
          if (hostname.includes('google')) return 'Google'
          if (hostname.includes('facebook') || hostname.includes('fb.com')) return 'Facebook'
          if (hostname.includes('twitter') || hostname.includes('t.co') || hostname.includes('x.com')) return 'X'
          if (hostname.includes('instagram')) return 'Instagram'
          if (hostname.includes('youtube')) return 'YouTube'
          if (hostname.includes('linkedin')) return 'LinkedIn'
          if (hostname.includes('tiktok')) return 'TikTok'
          if (hostname.includes('reddit')) return 'Reddit'
          return hostname
        } catch {
          return referrer || 'Direct / None'
        }
      }

      // Build referrer distribution with revenue
      const referrerDataMap = new Map<string, { visitors: number; revenue: number; conversions: number }>()
      const visitorReferrerSource = new Map<string, string>()
      for (const session of sessions) {
        const source = getReferrerSource(session.referrer)
        if (!visitorReferrerSource.has(session.visitor_hash)) {
          visitorReferrerSource.set(session.visitor_hash, source)
        }
        if (!referrerDataMap.has(source)) {
          referrerDataMap.set(source, { visitors: 0, revenue: 0, conversions: 0 })
        }
        referrerDataMap.get(source)!.visitors++
      }
      for (const rev of revenue) {
        const source = visitorReferrerSource.get(rev.visitor_hash) || 'Direct / None'
        const entry = referrerDataMap.get(source)
        if (entry) {
          entry.revenue += (rev.amount_cents || 0) / 100
          entry.conversions++
        }
      }
      const referrers = Array.from(referrerDataMap.entries())
        .map(([label, data]) => ({
          label,
          visitors: data.visitors,
          revenue: data.revenue,
          conversions: data.conversions,
          conversionRate: data.visitors > 0 ? (data.conversions / data.visitors) * 100 : 0,
          revenuePerVisitor: data.visitors > 0 ? data.revenue / data.visitors : 0,
        }))
        .sort((a, b) => b.visitors - a.visitors)
        .slice(0, 20)

      // Build campaign distribution with revenue (utm_campaign)
      const campaignDataMap = new Map<string, { visitors: number; revenue: number; conversions: number }>()
      for (const session of sessions) {
        if (session.utm_campaign) {
          if (!campaignDataMap.has(session.utm_campaign)) {
            campaignDataMap.set(session.utm_campaign, { visitors: 0, revenue: 0, conversions: 0 })
          }
          campaignDataMap.get(session.utm_campaign)!.visitors++
        }
      }
      for (const rev of revenue) {
        const campaign = visitorFieldValues.get(rev.visitor_hash)?.get('utm_campaign')
        if (campaign) {
          const entry = campaignDataMap.get(campaign)
          if (entry) {
            entry.revenue += (rev.amount_cents || 0) / 100
            entry.conversions++
          }
        }
      }
      const campaigns = Array.from(campaignDataMap.entries())
        .map(([label, data]) => ({
          label,
          visitors: data.visitors,
          revenue: data.revenue,
          conversions: data.conversions,
          conversionRate: data.visitors > 0 ? (data.conversions / data.visitors) * 100 : 0,
          revenuePerVisitor: data.visitors > 0 ? data.revenue / data.visitors : 0,
        }))
        .sort((a, b) => b.visitors - a.visitors)
        .slice(0, 20)

      // Build keywords distribution with revenue (utm_term)
      const keywordDataMap = new Map<string, { visitors: number; revenue: number; conversions: number }>()
      for (const session of sessions) {
        if (session.utm_term) {
          if (!keywordDataMap.has(session.utm_term)) {
            keywordDataMap.set(session.utm_term, { visitors: 0, revenue: 0, conversions: 0 })
          }
          keywordDataMap.get(session.utm_term)!.visitors++
        }
      }
      const keywords = Array.from(keywordDataMap.entries())
        .map(([label, data]) => ({
          label,
          visitors: data.visitors,
          revenue: data.revenue,
          conversions: data.conversions,
          conversionRate: data.visitors > 0 ? (data.conversions / data.visitors) * 100 : 0,
          revenuePerVisitor: data.visitors > 0 ? data.revenue / data.visitors : 0,
        }))
        .sort((a, b) => b.visitors - a.visitors)
        .slice(0, 20)

      setDistributions({
        referrers,
        campaigns,
        keywords,
        paths: buildDistributionWithRevenue('entry_page'),
        hostnames: [],
        entryPages: buildDistributionWithRevenue('entry_page'),
        exitPages: [],
        countries: buildDistributionWithRevenue('country_code'),
        regions: buildDistributionWithRevenue('region' as keyof Session),
        cities: buildDistributionWithRevenue('city'),
        browsers: buildDistributionWithRevenue('browser'),
        operatingSystems: buildDistributionWithRevenue('os'),
        devices: buildDistributionWithRevenue('device_type'),
      })

      // Build visitor rows (recent visitors)
      const visitorHashes = [...new Set(sessions.slice(0, 50).map((s) => s.visitor_hash))]
      const dogNameMap = createDogNameMap(visitorHashes)

      // Count visits per visitor
      const visitorHashCounts = new Map<string, number>()
      for (const session of sessions) {
        visitorHashCounts.set(session.visitor_hash, (visitorHashCounts.get(session.visitor_hash) || 0) + 1)
      }

      // Create map of converted visitor hashes to their revenue data
      const revenueByVisitor = new Map<string, { amount: number; currency: string; timestamp: string }>()
      for (const r of revenue) {
        const existing = revenueByVisitor.get(r.visitor_hash)
        if (!existing || r.amount_cents > existing.amount) {
          revenueByVisitor.set(r.visitor_hash, {
            amount: r.amount_cents / 100,
            currency: r.currency || 'USD',
            timestamp: r.timestamp,
          })
        }
      }

      // Track first visit time per visitor (for time to convert calculation)
      const firstVisitByVisitor = new Map<string, Date>()
      for (const session of sessions) {
        const startedAt = new Date(session.started_at)
        const existing = firstVisitByVisitor.get(session.visitor_hash)
        if (!existing || startedAt < existing) {
          firstVisitByVisitor.set(session.visitor_hash, startedAt)
        }
      }

      const visitorRows: VisitorRow[] = sessions.slice(0, 50).map((session) => {
        const lastSeenAt = new Date(session.last_seen_at)
        const startedAt = new Date(session.started_at)
        const hasRecentHeartbeat = lastSeenAt >= new Date(liveThreshold)
        const idleSeconds = session.idle_seconds || 0

        let status: VisitorStatus = 'offline'
        if (hasRecentHeartbeat) {
          status = idleSeconds >= IDLE_THRESHOLD_SECONDS ? 'inactive' : 'live'
        }

        const timeSpentSeconds = hasRecentHeartbeat
          ? Math.floor((now.getTime() - startedAt.getTime()) / 1000)
          : Math.floor((lastSeenAt.getTime() - startedAt.getTime()) / 1000)

        const revenueData = revenueByVisitor.get(session.visitor_hash)
        const firstVisit = firstVisitByVisitor.get(session.visitor_hash)
        let timeToConvert: number | undefined
        if (revenueData && firstVisit) {
          const convertedAt = new Date(revenueData.timestamp)
          timeToConvert = Math.max(0, Math.floor((convertedAt.getTime() - firstVisit.getTime()) / 1000))
        }

        return {
          sessionId: session.id,
          visitorHash: session.visitor_hash,
          dogName: dogNameMap.get(session.visitor_hash) || session.visitor_hash.slice(0, 8),
          countryCode: session.country_code,
          city: session.city,
          startedAt: session.started_at,
          lastSeenAt: session.last_seen_at,
          timeSpentSeconds: Math.max(0, timeSpentSeconds),
          status,
          visitCount: visitorHashCounts.get(session.visitor_hash) || 1,
          isConverted: revenueByVisitor.has(session.visitor_hash),
          browser: session.browser,
          os: session.os,
          deviceType: session.device_type,
          pageviewCount: session.pageview_count,
          eventCount: session.event_count,
          entryPage: session.entry_page,
          isBot: session.is_bot,
          revenueAmount: revenueData?.amount,
          revenueCurrency: revenueData?.currency,
          convertedAt: revenueData?.timestamp,
          timeToConvert,
          referrer: session.referrer,
        }
      })

      setVisitors(visitorRows)
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err)
      setError(err instanceof Error ? err : new Error('Failed to fetch data'))
    } finally {
      setIsLoading(false)
    }
  }, [siteId, dateFilter, drillDownFilter])

  fetchDataRef.current = fetchData

  // Initial fetch
  useEffect(() => {
    setIsLoading(true)
    fetchData()
  }, [fetchData])

  // Poll every 30 seconds (less frequent than before since we have more data)
  useEffect(() => {
    if (!siteId) return

    const interval = setInterval(() => fetchDataRef.current?.(), 30000)
    return () => clearInterval(interval)
  }, [siteId])

  return {
    kpis,
    timeSeries,
    distributions,
    visitors,
    isLoading,
    error,
    refresh: fetchData,
  }
}
